/**
 * The embed loader. This is what goes in the `<script>` tag — it must stay tiny.
 * It loads the heavy core bundle as soon as the page has a SplatView container.
 *
 *  - Multi-page sites: drop the snippet once in the global template. It fires on
 *    every page; pages without a container stay idle, pages with one load core.
 *  - SPA / client-routed sites: the container may be injected on a later route,
 *    so when none exists yet we watch the DOM and load core the moment the first
 *    one appears, then hand off (core runs its own observer for mount/unmount).
 *
 * PlayCanvas is never shipped in this file.
 */
(function (): void {
  const w = window as unknown as { __splatviewLoader?: boolean };
  if (w.__splatviewLoader) return; // snippet pasted twice → keep one loader
  w.__splatviewLoader = true;

  const SELECTOR = '[data-asset-id][data-license-key]';
  const self = document.currentScript as HTMLScriptElement | null;
  const src = self?.src ?? '';
  const coreUrl = src.replace(/widget\.js(\?.*)?$/, 'widget-core.js$1');

  if (!coreUrl || coreUrl === src) {
    // Could not derive the core URL from the script tag — bail loudly.
    console.error('SplatView: could not resolve core bundle URL from', src || '(unknown)');
    return;
  }

  let observer: MutationObserver | null = null;
  let loading = false;
  function loadCore(): void {
    if (loading) return;
    loading = true;
    observer?.disconnect();
    observer = null;
    import(/* @vite-ignore */ coreUrl).catch((err) => {
      console.error('SplatView: failed to load core bundle', err);
    });
  }

  if (document.querySelector(SELECTOR)) {
    loadCore();
    return;
  }

  // No container yet (SPA route not reached, or the snippet sits in <head> above
  // the container). Watch until the first one appears, then load core once.
  observer = new MutationObserver(() => {
    if (document.querySelector(SELECTOR)) loadCore();
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
